import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "@/components/ui/dialog";
import { login, register, formatApiError, forgotPassword } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Radio, GoogleLogo } from "@phosphor-icons/react";

export default function Login() {
  const { loginUser } = useAuth();
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  // Forgot-password modal state. Kept local because it's a one-shot flow
  // that doesn't need to survive page transitions.
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const fn = mode === "register" ? register : login;
      const payload =
        mode === "register"
          ? { email: email.trim().toLowerCase(), password, name: name.trim() }
          : { email: email.trim().toLowerCase(), password };
      const { token, user } = await fn(payload);
      loginUser(token, user);
      toast.success(`Welcome, ${user.name || user.email}`);
    } catch (e2) {
      const msg = formatApiError(e2?.response?.data?.detail) || "Auth failed";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotSending(true);
    try {
      await forgotPassword(forgotEmail.trim().toLowerCase());
      // Endpoint always returns 200 — show the same success state regardless
      // so we never reveal whether an account exists.
      setForgotSent(true);
    } catch (e2) {
      toast.error(formatApiError(e2?.response?.data?.detail) || "Could not send reset email");
    } finally {
      setForgotSending(false);
    }
  };

  const openForgot = () => {
    setForgotEmail(email);   // pre-fill with whatever they typed in the login form
    setForgotSent(false);
    setForgotOpen(true);
  };

  return (
    <div className="min-h-screen w-full bg-[#050505] text-white flex items-center justify-center relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-500/50 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-500/30 to-transparent" />

      {/* Two-column hero: brand logo on the left, login panel on the right.
          Both stay close to center via `justify-center` + a moderate gap so
          the eye reads them as a single block rather than spread to the edges.
          On narrow screens the logo collapses above the panel. */}
      <div className="relative px-6 py-8 flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10">
        <div className="flex justify-center shrink-0">
          <img
            src="/fire_dispatch_911_logo.png"
            alt="Fire Dispatch 911"
            data-testid="fire-dispatch-logo"
            className="w-56 sm:w-72 md:w-[26rem] lg:w-[28rem] h-auto select-none drop-shadow-[0_8px_24px_rgba(255,59,48,0.18)]"
            draggable={false}
          />
        </div>

        <div className="w-full max-w-md shrink-0">

        <div className="border border-white/10 bg-[#0F1115] p-6">
          <div className="flex items-center gap-2 mb-5 pb-4 border-b border-white/10">
            <Radio size={16} className="text-emerald-400" />
            <div className="mono text-[11px] uppercase tracking-widest text-zinc-400">
              CHANNEL OPEN — AUTHENTICATE
            </div>
            <div className="ml-auto flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-emerald-400 animate-pulse" />
              <span className="mono text-[10px] text-emerald-400">LIVE</span>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-5" data-testid="auth-tabs">
            <button
              data-testid="auth-tab-login"
              type="button"
              onClick={() => { setMode("login"); setErr(null); }}
              className={`flex-1 mono text-[11px] uppercase tracking-widest py-2 border ${
                mode === "login"
                  ? "border-red-500/70 text-red-300 bg-red-500/10"
                  : "border-white/10 text-zinc-500 hover:text-white"
              }`}
            >
              Sign In
            </button>
            <button
              data-testid="auth-tab-register"
              type="button"
              onClick={() => { setMode("register"); setErr(null); }}
              className={`flex-1 mono text-[11px] uppercase tracking-widest py-2 border ${
                mode === "register"
                  ? "border-emerald-500/70 text-emerald-300 bg-emerald-500/10"
                  : "border-white/10 text-zinc-500 hover:text-white"
              }`}
            >
              Create Account
            </button>
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div>
                <label className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">
                  Display Name
                </label>
                <Input
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Chief T."
                  maxLength={40}
                  required
                  className="rounded-none border-white/10 bg-black/40 mono tracking-wider focus-visible:ring-red-500/40 focus-visible:border-red-500/60"
                />
              </div>
            )}
            <div>
              <label className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">
                Email
              </label>
              <Input
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="chief@station.com"
                required
                autoComplete="email"
                className="rounded-none border-white/10 bg-black/40 mono tracking-wider focus-visible:ring-red-500/40 focus-visible:border-red-500/60"
              />
            </div>
            <div>
              <label className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">
                Password
              </label>
              <Input
                data-testid="auth-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                required
                minLength={6}
                autoComplete={mode === "register" ? "new-password" : "current-password"}
                className="rounded-none border-white/10 bg-black/40 mono tracking-wider focus-visible:ring-red-500/40 focus-visible:border-red-500/60"
              />
            </div>
            {err && (
              <div data-testid="auth-error" className="mono text-[10px] text-red-400 uppercase tracking-wider">
                {err}
              </div>
            )}
            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full rounded-none bg-red-600 hover:bg-red-500 text-white mono uppercase tracking-widest border border-red-500 h-10"
            >
              {loading ? "Connecting…" : mode === "register" ? "Create Account →" : "Begin Shift →"}
            </Button>
            {mode === "login" && (
              <div className="text-center pt-1">
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={openForgot}
                  className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-red-300 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
            )}
          </form>

          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="mono text-[10px] uppercase tracking-widest text-zinc-600">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <Button
            data-testid="google-signin-btn"
            type="button"
            onClick={googleSignIn}
            variant="outline"
            className="w-full rounded-none border-white/20 bg-white/5 hover:bg-white/10 text-white mono uppercase tracking-widest h-10"
          >
            <GoogleLogo size={16} weight="bold" className="mr-2" />
            Sign in with Google
          </Button>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-2 mono text-[10px] uppercase tracking-widest text-zinc-600">
          <div className="border border-white/5 p-2"><span className="text-zinc-400">SHIFT</span><br/>NIGHT</div>
          <div className="border border-white/5 p-2"><span className="text-zinc-400">SECTOR</span><br/>USA</div>
          <div className="border border-white/5 p-2"><span className="text-zinc-400">PROTOCOL</span><br/>FIRE/EMS</div>
        </div>
        </div> {/* /right column */}
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent
          className="rounded-none bg-[#0F1115] border-white/10 text-white max-w-md"
          data-testid="forgot-password-dialog"
        >
          <DialogHeader className="border-b border-white/10 pb-3">
            <div className="flex items-center gap-2">
              <Radio size={14} className="text-red-400" />
              <div className="mono text-[11px] uppercase tracking-widest text-zinc-400">
                {forgotSent ? "RESET LINK SENT" : "FORGOT PASSWORD"}
              </div>
            </div>
          </DialogHeader>
          {forgotSent ? (
            <div className="py-4 space-y-3">
              <p className="text-sm text-zinc-300 leading-relaxed">
                If an account exists for <span className="text-white font-semibold">{forgotEmail}</span>, a password reset link has been sent.
              </p>
              <p className="text-xs text-zinc-500 leading-relaxed">
                The link is valid for 1 hour. Check your inbox (and spam folder) for an email from <span className="mono">{`${"Dispatch 911"}`}</span>.
              </p>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="py-4 space-y-3">
              <p className="text-xs text-zinc-400 leading-relaxed">
                Enter the email address associated with your account. We'll send you a link to set a new password.
              </p>
              <div>
                <label className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">
                  Email
                </label>
                <Input
                  data-testid="forgot-email-input"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="chief@station.com"
                  required
                  autoFocus
                  className="rounded-none border-white/10 bg-black/40 mono tracking-wider focus-visible:ring-red-500/40 focus-visible:border-red-500/60"
                />
              </div>
              <DialogFooter className="border-t border-white/10 pt-3">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="forgot-cancel-btn"
                  onClick={() => setForgotOpen(false)}
                  className="rounded-none border-white/20 bg-white/5 hover:bg-white/10 text-white mono uppercase tracking-widest h-9"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="forgot-submit-btn"
                  disabled={forgotSending}
                  className="rounded-none bg-red-600 hover:bg-red-500 text-white mono uppercase tracking-widest border border-red-500 h-9"
                >
                  {forgotSending ? "Sending…" : "Send Reset Link"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
